import React, { useMemo } from 'react';

import { ClockIcon, DownloadIcon } from '@primer/octicons-react';
import { StyledOcticon, Timeline } from '@primer/react';

import { formatISOAsRelative } from '../../utils/date_utils';
import { truncateText } from '../../utils/text_utils';

import ClipboardLabel from '../common/ClipboardLabel/ClipboardLabel';
import CodeFormatLabel from '../common/Labels/CodeFormatLabel';
import DividerDot from '../common/misc/DividerDot';
import ErrorLabel from '../common/Labels/ErrorLabel';
import Label from '../common/Labels/Label';
import LinkLabel from '../common/Labels/LinkLabel';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import { preserveWorkspaceURL } from '../../utils/url_utils';

export const LoadingVersionItem = ({ condensed }) => (
  <Timeline.Item condensed={condensed}>
    <Timeline.Badge>
      <StyledOcticon icon={DownloadIcon} />
    </Timeline.Badge>
    <Timeline.Body>
      <div className="p-1">
        <LoadingSpinner animationKey="version-item-loading-spinner" />
      </div>
    </Timeline.Body>
  </Timeline.Item>
)

const ModelVersionItem = ({
  modelID,
  modelVersion,
  modelSHA,
  fileName,
  createdAt,
  condensed = false,
}) => {
  const truncatedSha = useMemo(() => truncateText(modelSHA, 16), [modelSHA]);

  return (
    <Timeline.Item className="text-brand items-center" condensed={condensed}>
      <Timeline.Badge>
        <StyledOcticon icon={ClockIcon} />
      </Timeline.Badge>
      <Timeline.Body>
        <div className="p-1 flex justify-between items-center">
          <div className="flex flex-col">
            <LinkLabel
              to={preserveWorkspaceURL(`/models/${modelID}/versions/${modelVersion}`)}
              bold
            >
              {modelVersion}
            </LinkLabel>
            {
              createdAt ? (
                <ErrorLabel className="ml-1" size="sm">created {formatISOAsRelative(createdAt)}</ErrorLabel>
              ) : null
            }
          </div>
          <div className="flex items-center">
            <Label className="mr-1">
              {fileName}
            </Label>
            <DividerDot />
            <ClipboardLabel
              className="p-1"
              iconSize={16}
              labelValue={modelSHA}
              displayLabel={truncatedSha}
              LabelComponent={CodeFormatLabel}
              showClipboardIconOnHover
            />
          </div>
        </div>
      </Timeline.Body>
    </Timeline.Item>
  );
}

const PipelineVersionItem = ({
  pipelineID,
  pipelineVersion,
  createdAt,
  condensed = false,
}) => (
  <Timeline.Item className="text-brand items-center" condensed={condensed}>
    <Timeline.Badge >
      <StyledOcticon icon={ClockIcon} />
    </Timeline.Badge>
    <Timeline.Body>
      <div className="p-1 flex justify-between items-center">
        <div className="flex flex-col">
          <LinkLabel
            to={preserveWorkspaceURL(`/pipelines/${pipelineID}/versions/${pipelineVersion}`)}
            bold
          >
            {pipelineVersion}
          </LinkLabel>
        </div>
        {
          createdAt ? (
            <ErrorLabel className="ml-1" size="sm">created {formatISOAsRelative(createdAt)}</ErrorLabel>
          ) : null
        }
      </div>
    </Timeline.Body>
  </Timeline.Item>
);

export const VersionHistoryItem = ({
  entry,
  modelID,
  pipelineID,
  condensed = false,
}) => {
  if (modelID) {
    return (
      <ModelVersionItem
        modelID={modelID}
        modelVersion={entry?.modelVersion}
        modelSHA={entry?.sha}
        fileName={entry?.fileName}
        createdAt={entry?.createdAt}
        condensed={condensed}
      />
    );
  }
  if (pipelineID) {
    return (
      <PipelineVersionItem
        pipelineID={pipelineID}
        pipelineVersion={entry?.version}
        createdAt={entry?.createdAt}
        condensed={condensed}
      />
    );
  }

  return <Timeline.Break />
}

export default VersionHistoryItem;
